import { t } from "@/i18n-js/instance";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react-uikit/Typography";
import { useDetermineConnectionStatus } from "../../hooks/useDetermineConnectionStatus";
import { MemberActions } from "./MemberActions";

interface CardHeaderProps {
  communityMember: any; // Because we have 3 different queries for the member directory, we need to use any here.
  shouldShowConnectionStatus?: boolean;
  shouldShowHiddenMemberTooltip?: boolean;
}

export const CardHeader = ({
  communityMember,
  shouldShowHiddenMemberTooltip = false,
  shouldShowConnectionStatus = false,
}: CardHeaderProps) => {
  const shouldShowHiddenMemberIcon =
    shouldShowHiddenMemberTooltip &&
    !communityMember.visible_in_member_directory;
  const renderTagline = () => communityMember.headline;
  const isSmScreen = useSmScreenMediaQuery();

  const { isActive: isConnected } = useDetermineConnectionStatus(
    communityMember.connection_to_current_community_member,
  );

  const MemberResponsiveTitleComponent = isSmScreen
    ? Typography.LabelSm
    : Typography.LabelLg;

  return (
    <div className="flex items-start justify-between">
      <div className="flex flex-col gap-y-0 md:gap-y-1">
        <header className="flex items-center gap-x-2" data-testid="member-name">
          <MemberResponsiveTitleComponent weight="semibold">
            {communityMember.name}
          </MemberResponsiveTitleComponent>
          {shouldShowHiddenMemberIcon && (
            <div
              aria-label={t(
                "members_directory.community_members.hidden_profile",
              )}
            >
              <TippyV2
                placement="right"
                content={t(
                  "members_directory.community_members.hidden_profile",
                )}
              >
                <Icon type="eye-closed-solid" size={16} />
              </TippyV2>
            </div>
          )}
        </header>
        <div className="text-default hidden text-left md:flex">
          <Typography.LabelXs>{renderTagline()}</Typography.LabelXs>
        </div>
        <span className="text-default flex md:hidden">
          <Typography.LabelXs>{communityMember.email}</Typography.LabelXs>
        </span>
      </div>

      <div className="flex flex-col items-end gap-x-2 gap-y-1 md:flex-row md:items-center">
        {shouldShowConnectionStatus && isConnected && (
          <div className="bg-secondary text-dark flex !h-[22px] items-center justify-center gap-1 rounded-lg px-2 py-[2px] font-medium">
            <Icon type="20-checkmark" size={20} className="h-4 w-4" />
            <Typography.LabelXs weight="medium">
              {t("profile_drawer.connect_banner.states.active")}
            </Typography.LabelXs>
          </div>
        )}
        <MemberActions communityMember={communityMember} />
      </div>
    </div>
  );
};
